import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography
} from '@material-ui/core';
import { SendIcon, TimesIcon, PlusIcon } from '../../icons';
import { useStyles } from './chatStyle';
import { useSelector } from 'react-redux';
import { agentsInChainAutocomplete } from '../../autocomplete';
import { appendToThread, safeParseThreads, pickUserFromAutocomplete } from './chatHelpers';
import { useChatSocket } from './hooks';
import moment from '../../moment-hawaii';
import config from '../../config';

const CloseIcon = TimesIcon;
const AddIcon = PlusIcon;
const THREAD_KEY = 'chat_threads_v1';
const CHAT_URL_V1 = config.chatUrl;
const PAGE_SIZE = 50;
const LOAD_OLDER_THRESHOLD_PX = 80;

const parseErrorMessage = async res => {
    const raw = await res.text();
    let message = `HTTP ${res.status}`;

    try {
        const parsed = JSON.parse(raw);
        message = parsed?.message || message;
    } catch {}

    return message;
};

const toDisplayMessage = msg => ({
    id: msg.id,
    from: msg.from,
    text: msg.text,
    ts: msg.ts || msg.created_at
});

const ChatWindow = ({
    isChatOpen,
    setIsChatOpen,
    unreadByUser = {},
    setUnreadByUser = () => {}
}) => {
    const classes = useStyles();
    const scrollerRef = useRef(null);
    const [draft, setDraft] = useState('');

    const user = useSelector(state => state.user?.user);

    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isNewChatOpen, setIsNewChatOpen] = useState(false);
    const [newChatSearch, setNewChatSearch] = useState('');
    const [newChatResults, setNewChatResults] = useState([]);
    const [chats, setChats] = useState([]);
    const [threads, setThreads] = useState(() =>
        safeParseThreads(localStorage.getItem(THREAD_KEY))
    );
    const [hasMoreByUser, setHasMoreByUser] = useState({});
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const lastMessageIdRef = useRef(null);

    const selectedChat = selectedUserId ? chats.find(c => c.id === selectedUserId) : null;
    const messages = selectedUserId ? threads[selectedUserId] || [] : [];
    const selectedUserName = selectedChat?.name || selectedUserId || '';

    const setThreadsAndBroadcast = useCallback(updater => {
        setThreads(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            localStorage.setItem(THREAD_KEY, JSON.stringify(next));
            return next;
        });
    }, []);

    const clearUnreadFor = useCallback(
        userId => {
            if (!userId) {
                return;
            }
            const key = String(userId);
            setUnreadByUser(prev => {
                if (!prev || !prev[key]) {
                    return prev || {};
                }
                const next = { ...prev };
                delete next[key];
                return next;
            });
        },
        [setUnreadByUser]
    );

    const selectChat = useCallback(
        userId => {
            const id = String(userId);
            setSelectedUserId(id);
            setIsNewChatOpen(false);
            clearUnreadFor(id);
        },
        [clearUnreadFor]
    );

    const onPick = val =>
        pickUserFromAutocomplete({
            val,
            setChats,
            setSelectedUserId: id => {
                setSelectedUserId(String(id));
                clearUnreadFor(id);
            },
            setIsAutocompleteOpen: setIsNewChatOpen
        });

    // Keep refs of the current dock/selection state so the message handler always reads fresh values.
    const isChatOpenRef = useRef(isChatOpen);
    const selectedUserIdRef = useRef(selectedUserId);
    const isNewChatOpenRef = useRef(isNewChatOpen);
    useEffect(() => {
        isChatOpenRef.current = isChatOpen;
    }, [isChatOpen]);
    useEffect(() => {
        selectedUserIdRef.current = selectedUserId;
    }, [selectedUserId]);
    useEffect(() => {
        isNewChatOpenRef.current = isNewChatOpen;
    }, [isNewChatOpen]);
    const threadsRef = useRef(threads);
    useEffect(() => {
        threadsRef.current = threads;
    }, [threads]);
    const selectedChatRef = useRef(selectedChat);
    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    const shouldCountUnread = useCallback(otherUserId => {
        return !(
            isChatOpenRef.current &&
            !isNewChatOpenRef.current &&
            String(selectedUserIdRef.current) === String(otherUserId)
        );
    }, []);

    useEffect(() => {
        if (!isNewChatOpen) {
            setNewChatSearch('');
            setNewChatResults([]);
            return;
        }
        agentsInChainAutocomplete
            .search(newChatSearch, 20)
            .then(results => setNewChatResults(results.filter(r => r.id !== 0))); // 0 is the customer
    }, [isNewChatOpen, newChatSearch]);

    const usersForSidebar = useMemo(() => {
        return [...chats]
            .map(c => ({
                id: c.id,
                name: c.name ?? c.id,
                online: c.online,
                unread: unreadByUser[String(c.id)] || 0
            }))
            .sort((a, b) => {
                const aUnread = a.unread > 0;
                const bUnread = b.unread > 0;
                if (aUnread !== bUnread) {
                    return aUnread ? -1 : 1;
                }

                if (a.online !== b.online) {
                    return a.online ? -1 : 1;
                }

                return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
            });
    }, [chats, unreadByUser]);

    const socket = useChatSocket(user?.token);

    useEffect(() => {
        const loadChats = async () => {
            try {
                const res = await fetch(`${CHAT_URL_V1}/chats`, {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                });

                if (!res.ok) {
                    throw new Error(await parseErrorMessage(res));
                }
                const data = await res.json();

                if (!Array.isArray(data)) {
                    throw new Error('Chats response is not an array');
                }

                const newChats = [];

                for (const chat of data) {
                    const id = String(chat.user_id);

                    newChats.push({
                        id,
                        name: chat.name ?? id,
                        online: chat.status?.status === 'online'
                    });
                }

                setThreadsAndBroadcast(prev => {
                    const next = { ...prev };

                    for (const chat of data) {
                        const id = String(chat.user_id);

                        if (!next[id]) {
                            next[id] = [];
                        }
                    }

                    return next;
                });

                setChats(newChats);
            } catch (err) {
                console.error('Failed to load chats', err);
            }
        };

        if (user?.token) {
            loadChats();
        }
    }, [user?.token]);

    // Auto-select the first chat when nothing is selected so the right pane isn't empty.
    // Only clear unread if the dock is open — otherwise a badge from a prior session
    // would be wiped silently before the user ever sees it.
    useEffect(() => {
        if (isNewChatOpen || selectedUserId) {
            return;
        }
        if (usersForSidebar.length === 0) {
            return;
        }
        const first = usersForSidebar[0];
        setSelectedUserId(String(first.id));
        if (isChatOpen) {
            clearUnreadFor(first.id);
        }
    }, [isChatOpen, isNewChatOpen, selectedUserId, usersForSidebar, clearUnreadFor]);

    // Clear unread for the currently visible chat whenever the dock opens or selection changes.
    useEffect(() => {
        if (!isChatOpen || !selectedUserId || isNewChatOpen) {
            return;
        }
        clearUnreadFor(selectedUserId);
    }, [isChatOpen, selectedUserId, isNewChatOpen, clearUnreadFor]);

    const mergeMessagesById = (current, incoming) => {
        const byId = new Map();

        [...current, ...incoming].forEach(message => {
            byId.set(String(message.id), message);
        });

        return Array.from(byId.values()).sort((a, b) => Number(a.id) - Number(b.id));
    };

    const incrementUnread = useCallback(
        (otherUserId, amount = 1) => {
            if (!otherUserId || amount <= 0) {
                return;
            }

            setUnreadByUser(prev => {
                const base = prev && typeof prev === 'object' && !Array.isArray(prev) ? prev : {};
                const current = Number(base[otherUserId]) || 0;

                return {
                    ...base,
                    [otherUserId]: current + amount
                };
            });
        },
        [setUnreadByUser]
    );

    useEffect(() => {
        const loadMessages = async () => {
            if (!selectedChatRef.current || !user?.token || !selectedUserId) {
                return;
            }

            const existingMessages = threadsRef.current[selectedUserId] || [];
            const latestId = existingMessages[existingMessages.length - 1]?.id;

            const query = latestId
                ? `userId=${selectedUserId}&after=${latestId}&limit=${PAGE_SIZE}`
                : `userId=${selectedUserId}&limit=${PAGE_SIZE}`;

            try {
                const res = await fetch(`${CHAT_URL_V1}/messages?${query}`, {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                });

                if (!res.ok) {
                    throw new Error(await parseErrorMessage(res));
                }

                const data = await res.json();

                if (!data || !Array.isArray(data.messages)) {
                    throw new Error('Messages response is malformed');
                }

                const fetchedMessages = latestId
                    ? data.messages.map(toDisplayMessage)
                    : [...data.messages].reverse().map(toDisplayMessage);

                setThreadsAndBroadcast(prev => {
                    const current = prev[selectedUserId] || [];

                    return {
                        ...prev,
                        [selectedUserId]: mergeMessagesById(current, fetchedMessages)
                    };
                });

                if (latestId) {
                    const unreadIncoming = fetchedMessages.filter(m => m.from !== 'me').length;

                    if (shouldCountUnread(selectedUserId)) {
                        incrementUnread(selectedUserId, unreadIncoming);
                    }
                }

                setHasMoreByUser(prev => ({
                    ...prev,
                    [selectedUserId]: latestId ? prev[selectedUserId] : Boolean(data.hasMore)
                }));
            } catch (err) {
                console.error('Failed to load messages', err);
            }
        };

        loadMessages();
    }, [user?.token, selectedUserId, setThreadsAndBroadcast, shouldCountUnread, incrementUnread]);

    const loadOlderMessages = useCallback(async () => {
        if (isLoadingOlder || !selectedUserId || !user?.token) {
            return;
        }
        if (!hasMoreByUser[selectedUserId]) {
            return;
        }

        const currentThread = threads[selectedUserId] || [];
        const oldestId = currentThread[0]?.id;
        if (!oldestId) {
            return;
        }

        const el = scrollerRef.current;
        const prevScrollHeight = el?.scrollHeight ?? 0;
        const prevScrollTop = el?.scrollTop ?? 0;

        setIsLoadingOlder(true);

        try {
            const res = await fetch(
                `${CHAT_URL_V1}/messages?userId=${selectedUserId}&before=${oldestId}&limit=${PAGE_SIZE}`,
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            if (!res.ok) {
                throw new Error(await parseErrorMessage(res));
            }
            const data = await res.json();

            if (!data || !Array.isArray(data.messages)) {
                throw new Error('Messages response is malformed');
            }

            const olderAsc = [...data.messages].reverse().map(toDisplayMessage);

            setThreadsAndBroadcast(prev => ({
                ...prev,
                [selectedUserId]: [...olderAsc, ...(prev[selectedUserId] || [])]
            }));

            setHasMoreByUser(prev => ({
                ...prev,
                [selectedUserId]: Boolean(data.hasMore)
            }));

            requestAnimationFrame(() => {
                const node = scrollerRef.current;
                if (!node) {
                    return;
                }
                node.scrollTop = node.scrollHeight - prevScrollHeight + prevScrollTop;
            });
        } catch (err) {
            console.error('Failed to load older messages', err);
        } finally {
            setIsLoadingOlder(false);
        }
    }, [
        isLoadingOlder,
        selectedUserId,
        user?.token,
        hasMoreByUser,
        threads,
        setThreadsAndBroadcast
    ]);

    const onMessagesScroll = e => {
        if (e.currentTarget.scrollTop <= LOAD_OLDER_THRESHOLD_PX) {
            loadOlderMessages();
        }
    };

    useEffect(() => {
        const onStorage = e => {
            if (e.key !== THREAD_KEY) {
                return;
            }
            setThreads(safeParseThreads(e.newValue));
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }
        el.scrollTop = el.scrollHeight;
        lastMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserId]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }
        const lastId = messages[messages.length - 1]?.id ?? null;
        if (lastId === lastMessageIdRef.current) {
            return;
        }
        lastMessageIdRef.current = lastId;

        const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
        if (isNearBottom) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages]);

    const handleSend = () => {
        const text = draft.trim();
        if (!text || !selectedChat) {
            return;
        }

        const fromDisplayName = [user?.firstname, user?.lastname]
            .filter(Boolean)
            .join(' ')
            .trim();

        socket.emit('chat:input', {
            to: selectedUserId,
            to_display_name:
                chats.find(c => String(c.id) === String(selectedUserId))?.name || selectedUserId,
            from_display_name: fromDisplayName,
            text
        });

        setDraft('');
    };

    useEffect(() => {
        const onNew = msg => {
            const myId = String(user.id);
            const isMe = String(msg.from) === myId;

            const otherUserId = isMe ? String(msg.to) : String(msg.from);

            const newMessage = {
                id: msg.id,
                from: isMe ? 'me' : 'them',
                text: msg.text,
                ts: msg.ts || msg.created_at
            };

            setThreadsAndBroadcast(prev => appendToThread(prev, otherUserId, newMessage));

            setChats(prev => {
                const existing = prev.find(c => String(c.id) === otherUserId);
                const chatName =
                    (isMe ? msg.receiver_display_name : msg.sender_display_name) ??
                    existing?.name ??
                    otherUserId;

                if (existing) {
                    return prev.map(c =>
                        String(c.id) === otherUserId ? { ...c, name: chatName } : c
                    );
                }

                return [...prev, { id: otherUserId, name: chatName }];
            });

            if (isMe) {
                return;
            }

            if (!shouldCountUnread(otherUserId)) {
                return;
            }

            incrementUnread(otherUserId, 1);
        };

        socket.on('message:new', onNew);
        return () => socket.off('message:new', onNew);
    }, [socket, user?.id, setThreadsAndBroadcast, shouldCountUnread, incrementUnread]);

    useEffect(() => {
        const onStatus = payload => {
            if (!payload || payload.userId == null) {
                return;
            }
            const id = String(payload.userId);
            const online = payload.status === 'online';
            setChats(prev => {
                if (!prev.some(c => c.id === id)) {
                    return prev;
                }
                return prev.map(c => (c.id === id ? { ...c, online } : c));
            });
        };

        socket.on('user_status', onStatus);
        return () => socket.off('user_status', onStatus);
    }, [socket]);

    const onKeyDown = e => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const selectedUser = useMemo(() => {
        return usersForSidebar.find(u => u.id === selectedUserId) || null;
    }, [usersForSidebar, selectedUserId]);

    const unreadElsewhere = useMemo(() => {
        return Object.entries(unreadByUser).reduce((sum, [id, n]) => {
            if (id === String(selectedUserId)) {
                return sum;
            }
            const v = Number(n);
            return sum + (Number.isFinite(v) && v > 0 ? v : 0);
        }, 0);
    }, [unreadByUser, selectedUserId]);

    const renderSearchPane = () => (
        <div className={classes.searchPane}>
            <div className={classes.chatHeader}>
                <div className={classes.titleAndStatus}>
                    <Typography className={classes.chatTitle} variant="subtitle1">
                        Start a new chat
                    </Typography>
                </div>
                <div className={classes.chatWindowClose}>
                    <IconButton size="small" onClick={() => setIsNewChatOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            </div>
            <Divider />
            <div className={classes.searchField}>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search agents..."
                    value={newChatSearch}
                    onChange={e => setNewChatSearch(e.target.value)}
                    fullWidth
                    autoFocus
                />
            </div>
            <List className={classes.searchResults} disablePadding>
                {newChatResults.map(agent => (
                    <ListItem
                        key={agent.id}
                        button
                        className={`${classes.searchItem} ${classes.searchItemIndicator}`}
                        onClick={() => onPick(agent)}
                    >
                        <ListItemText className={classes.userItemText} primary={agent.name} />
                    </ListItem>
                ))}
                {newChatResults.length === 0 && (
                    <div className={classes.searchEmpty}>
                        {newChatSearch ? 'No agents found' : 'Type a name to search for an agent'}
                    </div>
                )}
            </List>
        </div>
    );

    const renderChatPane = () => (
        <div className={classes.chat}>
            <div className={classes.chatHeader}>
                <div className={classes.titleAndStatus}>
                    <Typography className={classes.chatTitle} variant="subtitle1">
                        {selectedUserName}
                    </Typography>
                    <Typography className={classes.status}>
                        {selectedUser ? (selectedUser.online ? 'online' : 'offline') : ''}
                    </Typography>
                    {unreadElsewhere > 0 && (
                        <span
                            className={classes.headerUnreadHint}
                            aria-label={`${unreadElsewhere} unread in other chats`}
                        >
                            +{unreadElsewhere > 9 ? '9+' : unreadElsewhere}
                        </span>
                    )}
                </div>

                <div className={classes.chatWindowClose}>
                    <IconButton size="small" onClick={() => setIsChatOpen(prev => !prev)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            </div>
            <Divider />
            {selectedChat ? (
                <>
                    <div
                        className={classes.messagesWrap}
                        ref={scrollerRef}
                        onScroll={onMessagesScroll}
                    >
                        {messages.map(m => {
                            const isMe = m.from === 'me';
                            return (
                                <div
                                    key={m.id}
                                    className={`${classes.bubbleRow} ${
                                        isMe ? classes.bubbleRowMe : ''
                                    }`}
                                >
                                    <div
                                        className={`${classes.bubble} ${
                                            isMe ? classes.bubbleMe : classes.bubbleOther
                                        }`}
                                    >
                                        <Typography className={classes.bubbleText}>
                                            {m.text}
                                        </Typography>
                                        <div className={classes.bubbleMeta}>
                                            {moment(m.ts).format('ddd, MMM DD, YYYY [at] hh:mm a')}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className={classes.inputBar}>
                        <TextField
                            className={classes.input}
                            variant="outlined"
                            size="small"
                            placeholder="Type a message..."
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            onKeyDown={onKeyDown}
                            multiline
                            maxRows={3}
                        />
                        <IconButton onClick={handleSend} aria-label="send">
                            <SendIcon />
                        </IconButton>
                    </div>
                </>
            ) : (
                <div className={classes.emptyMessages}>
                    No chats yet. Use the + button to start one.
                </div>
            )}
        </div>
    );

    return (
        <Paper className={classes.root} elevation={2}>
            <div className={classes.sidebar}>
                <div className={classes.sidebarHeader}>
                    <Typography className={classes.sidebarHeaderTitle} variant="subtitle1">
                        Chats
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => setIsNewChatOpen(prev => !prev)}
                        aria-label={isNewChatOpen ? 'Cancel new chat' : 'Start new chat'}
                    >
                        {isNewChatOpen ? (
                            <CloseIcon fontSize="small" />
                        ) : (
                            <AddIcon fontSize="small" />
                        )}
                    </IconButton>
                </div>
                <Divider />
                <List className={classes.usersList} disablePadding>
                    {usersForSidebar.map(u => {
                        const selected = u.id === selectedUserId && !isNewChatOpen;
                        const unread = u.unread || 0;
                        const statusClass = u.online
                            ? classes.userItemOnline
                            : classes.userItemOffline;
                        return (
                            <ListItem
                                key={u.id}
                                button
                                className={`${classes.userItem} ${statusClass} ${
                                    selected ? classes.userItemSelected : ''
                                }`}
                                onClick={() => selectChat(u.id)}
                            >
                                <ListItemText
                                    className={`${classes.userItemText} ${
                                        unread > 0 ? classes.userItemTextUnread : ''
                                    }`}
                                    primary={u.name}
                                />
                                {unread > 0 && (
                                    <span className={classes.unreadPill}>
                                        {unread > 9 ? '9+' : unread}
                                    </span>
                                )}
                            </ListItem>
                        );
                    })}
                    {usersForSidebar.length === 0 && (
                        <div className={classes.searchEmpty}>No chats yet</div>
                    )}
                </List>
            </div>
            {isNewChatOpen ? renderSearchPane() : renderChatPane()}
        </Paper>
    );
};

export default ChatWindow;
